<template>
  <div class="asc-fault-msg-code-grid">
    <div class="asc-fault-msg-code-grid--header">
      <asc-header
        class="asc-fault-msg-code-grid--header-title"
        uppercase
        size="h4"
        :value="title"
      />
    </div>

    <asc-ag-grid-filter
      :key="`asc-fault-msg-code-grid_${gridId}`"
      :grid-options="agGridOptions"
      :column-defs="getGridColumnDefs"
      :row-data="agGridRowData"
    ></asc-ag-grid-filter>
  </div>
</template>

<script>
import {
  AscAgGridFilter,
  AscAgGridAutocompleteFloatFilter,
  AscAgGridDateFloatFilter,
  AscTooltipCellRenderer,
  AscNestedCellRenderer,
} from '@/components/agGrid'
import { AscGridPropsMixin, AscGridMethodsMixin } from '@/components/mixins'
import { getFormattedDateTime } from '@/utilities/dates'
import AscHeader from '@/components/atoms/AscHeader'
import {
  stringTextMatcher,
  dateStringMatcher,
  exactStringMatcher,
} from '@/components/agGrid/AgGridHelperMethods'
import { getFilterOptions } from '@/components/agGrid/AgGridOptions'

export default {
  name: 'AscFaultMsgCodeGrid',

  components: {
    AscAgGridFilter,
    AscHeader,
    AscAgGridAutocompleteFloatFilter,
    AscAgGridDateFloatFilter,
    AscTooltipCellRenderer,
    AscNestedCellRenderer,
  },

  mixins: [AscGridPropsMixin, AscGridMethodsMixin],

  props: {
    title: {
      type: String,
      default: '',
    },
    messageCodes: {
      type: Array,
      default: () => [],
    },
    gridColumns: {
      type: Array,
      default: () => [],
    },
    onOpenRawReport: {
      type: Function,
      default: () => {},
    },
    onVisualize: {
      type: Function,
      default: () => {},
    },
  },

  data() {
    return {
      updateDateFieldName: 'timeStamp',
      agGridOptions: getFilterOptions(this, {
        defaultColDef: {
          floatingFilter: true,
          tooltipComponent: 'AscTooltipCellRenderer',
        },
        getContextMenuItems: this.getContextMenuItems,
        treeData: true,
        groupDefaultExpanded: -1,
        getDataPath: function (data) {
          return data.path
        },
        autoGroupColumnDef: {
          headerName: 'Type',
          minWidth: 120,
          cellRenderer: 'agGroupCellRenderer',
          cellRendererParams: {
            suppressCount: true,
            innerRenderer: 'AscNestedCellRenderer',
            handleArrowClicked: this.handleArrowClicked,
          },
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompleteFloatFilter',
          floatingFilterComponentParams: {
            ids: [],
            items: ['FDE', 'MMO', 'NCMM', 'CMM'].map(item => {
              return {
                text: item,
                value: item,
              }
            }),
            suppressFilterButton: true,
          },
          valueGetter: params => params.data?.errorType,
        },

        onGridReady: this.handleGridReady,
        onFirstDataRendered: this.handleFirstDataRendered,
        onGridSizeChanged: this.handleGridSizeChanged,
        onCellClicked: this.handleCellClicked,
      }),
    }
  },

  computed: {
    mappedValues() {
      return this.values.map(item => {
        return {
          ...item,
          code: item.code.toString(),
        }
      })
    },

    agGridRowData() {
      return this.mappedValues
    },

    getGridColumnDefs() {
      return [
        {
          headerName: this.getColumnTitle(this.gridColumns, 'code', 'Code'),
          field: 'code',
          hide: !this.isColumnVisible(this.gridColumns, 'code'),
          filter: 'agTextColumnFilter',
          cellStyle: params => {
            if (this.messageCodes.includes(params.value)) {
              return { fontWeight: 'bold' }
            }
            return null
          },
          filterParams: {
            textMatcher: exactStringMatcher,
          },
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'flightPhase',
            'Flight Phase',
          ),
          field: 'flightPhase',
          filter: 'agTextColumnFilter',
          hide: !this.isColumnVisible(this.gridColumns, 'flightPhase'),
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompleteFloatFilter',
          floatingFilterComponentParams: {
            ids: [],
            items: this.getUniqueItems('flightPhase'),
            suppressFilterButton: true,
          },
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'flightNumber',
            'Flight #',
          ),
          field: 'flightNumber',
          filter: 'agTextColumnFilter',
          hide: !this.isColumnVisible(this.gridColumns, 'flightNumber'),
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompleteFloatFilter',
          floatingFilterComponentParams: {
            ids: [],
            items: this.getUniqueItems('flightNumber'),
            suppressFilterButton: true,
          },
        },
        {
          headerName: this.getColumnTitle(this.gridColumns, 'state', 'State'),
          field: 'state',
          filter: 'agTextColumnFilter',
          hide: !this.isColumnVisible(this.gridColumns, 'state'),
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompleteFloatFilter',
          floatingFilterComponentParams: {
            ids: [],
            items: this.getUniqueItems('state'),
            suppressFilterButton: true,
          },
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'persistence',
            'Persistence',
          ),
          field: 'persistence',
          minWidth: 120,
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: stringTextMatcher,
          },
          floatingFilterComponent: 'AscAgGridAutocompleteFloatFilter',
          floatingFilterComponentParams: {
            ids: [],
            items: this.getUniqueItems('persistence'),
            suppressFilterButton: true,
          },
          cellStyle: { textAlign: 'center' },
          hide: !this.isColumnVisible(this.gridColumns, 'persistence'),
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'timeStamp',
            'Time Stamp',
          ),
          field: 'timeStamp',
          minWidth: 155,
          sort: 'desc',
          filter: 'agTextColumnFilter',
          filterParams: {
            textMatcher: dateStringMatcher,
          },
          floatingFilterComponent: 'AscAgGridDateFloatFilter',
          floatingFilterComponentParams: {
            suppressFilterButton: true,
            items: this.values,
          },
          valueFormatter: data => getFormattedDateTime(data.value),
          comparator: this.sortDate,
          hide: !this.isColumnVisible(this.gridColumns, 'timeStamp'),
        },
        {
          headerName: this.getColumnTitle(this.gridColumns, 'report', 'Report'),
          field: '',
          minWidth: 125,
          width: 125,
          cellClass: 'link-grid-cell',
          cellRenderer: () => {
            return `<label>View Report</label><i aria-hidden="true" class="v-icon notranslate mdi mdi-chevron-right" style="font-size: 20px;"></i>`
          },
          onCellClicked: params => this.handleViewRawReportClick(params),
          sortable: false,
          hide: !this.isColumnVisible(this.gridColumns, 'report'),
        },
        {
          headerName: this.getColumnTitle(
            this.gridColumns,
            'visualize',
            'Visualize',
          ),
          field: '',
          minWidth: 115,
          width: 115,
          cellClass: 'link-grid-cell',
          cellRenderer: () => {
            return `<label>Visualize</label><i aria-hidden="true" class="v-icon notranslate mdi mdi-chevron-right" style="font-size: 20px;"></i>`
          },
          onCellClicked: params => this.handleVisualizeClick(params),
          sortable: false,
          hide: !this.isColumnVisible(this.gridColumns, 'visualize'),
        },
      ]
    },
  },

  watch: {
    values() {
      this.handleRefreshColWidths()
    },
  },

  methods: {
    getContextMenuItems() {
      const result = ['copy', 'copyWithHeaders', 'export']

      return result
    },

    handleVisualizeClick(params) {
      if (this.onVisualize) {
        this.onVisualize(params.data)
      }
    },

    handleViewRawReportClick(params) {
      if (this.onOpenRawReport) {
        this.onOpenRawReport(params.data)
      }
      this.$emit('onReportClick', params.data)
    },

    handleArrowClicked(data) {
      this.agGridOptions.api.setRowNodeExpanded(data.node, !data.node.expanded)
    },
  },
}
</script>

<style lang="scss">
.asc-fault-msg-code-grid {
  min-height: 600px;

  &--container {
    padding: 0 10px;
  }

  &--header {
    display: flex;
    padding-top: 10px;
    padding-left: 10px;
    margin-bottom: 16px;

    &-title {
      & .asc-header--h4 {
        font-size: 14px !important;
        font-weight: 500 !important;
        line-height: 20px !important;
        letter-spacing: 1px !important;
      }
    }
  }

  .link-grid-cell {
    cursor: pointer;

    label {
      cursor: pointer;
      color: $accent-700;
    }

    i.v-icon {
      cursor: pointer;
      color: $accent-700;

      &.mdi-pencil {
        display: none;
      }

      &.mdi-chevron-right {
        margin-left: 5.3px;
      }
    }

    &:hover {
      background-color: $accent-0;

      label {
        text-decoration: underline;
      }

      i.v-icon {
        &.mdi-pencil {
          position: absolute;
          margin-left: 5px;
          display: inline;
        }
      }
    }
  }

  .ag-theme-balham {
    .ag-header-row-floating-filter {
      top: 40px !important;
      height: 40px !important;
    }

    .ag-cell {
      border-top: 1px solid $gray-300 !important;
      border-bottom: none !important;
    }

    .ag-row-first > .ag-cell {
      border-top: none !important;
    }

    .ag-row-last > .ag-cell {
      border-bottom: 1px solid $gray-300 !important;
    }

    .ag-ltr .ag-row-group-leaf-indent {
      margin-left: 0;
    }

    .ag-cell-wrapper > *:not(.ag-cell-value):not(.ag-group-value) {
      display: none;
    }
  }

  .asc-ag-grid-filter--container .ag-theme-balham {
    .ag-row-level-1 > .ag-cell {
      border-top: none !important;
    }
  }
}
</style>
